import type { ReactNode } from 'react'
import { useTranslation, type UiTFunction } from '../i18n'
import { COLORS } from '../lib/theme'
import { LiveDot } from './LiveDot'
import { TopStatsSection } from './TopStatsSection'
import { SeasonSummaryCard } from './SeasonSummaryCard'
import { ActivityWeekCard } from './ActivityWeekCard'
import type { Team, Hunt } from '../lib/types'

export interface OverviewPageProps {
  user: { name: string }
  teams: Team[]
  hunts: Hunt[]
  onOpenHunt: (id: string) => void
  topStatsSection?: ReactNode
  seasonSection?: ReactNode
  activityPanel?: ReactNode
  /**
   * CLUB-138: the Activity overview panel is hidden in the general view for now
   * (it surfaced dog-related content that isn't appropriate here). Kept behind a
   * flag rather than deleted so it can be brought back once the data is fixed —
   * defaults to hidden. When false, the Upcoming hunts card reflows to full width.
   */
  showActivityPanel?: boolean
}

function greeting(t: UiTFunction, name: string) {
  const h = new Date().getHours()
  const part = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening'
  const first = name.split(' ')[0]
  return t(`greeting.${part}`, { name: first })
}

function huntHours(h: Hunt): number {
  const [sh, sm] = h.start.split(':').map(Number)
  const [eh, em] = h.end!.split(':').map(Number)
  return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60)
}

function huntLabel(h: Hunt): string {
  const d = new Date(h.date)
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

export function OverviewPage({ user, teams, hunts, onOpenHunt, topStatsSection, seasonSection, activityPanel, showActivityPanel = false }: OverviewPageProps) {
  const { t } = useTranslation('overview')
  const ended = hunts.filter((h) => h.status === 'ended')
  const ongoing = hunts.filter((h) => h.status === 'ongoing')
  const upcoming = hunts.filter((h) => h.status === 'upcoming').slice(0, 3)

  const lastTeamId =
    typeof window !== 'undefined' ? window.localStorage.getItem('wehunt:lastTeamId') : null
  const lastTeam = teams.find((t) => t.id === lastTeamId) ?? teams[0]

  const totalHa = teams.reduce((s, t) => s + (t.hectares ?? 0), 0)
  const seasonHours = Math.round(ended.reduce((s, h) => s + huntHours(h), 0))
  const downed = ended.reduce((s, h) => s + (h.harvest?.reduce((a, g) => a + g.count, 0) ?? 0), 0)
  const observed = Math.round(downed * 3)

  const defaultTopStats = (
    <TopStatsSection
      teamCount={teams.length}
      totalHectares={totalHa}
      groundCount={teams.length}
      lastTeamId={lastTeam?.id}
      lastTeamName={lastTeam?.name}
    />
  )

  const defaultSeason = (
    <SeasonSummaryCard
      huntsAttended={ended.length}
      hoursInField={seasonHours}
      gameReported={downed + observed}
      downed={downed}
      observed={observed}
      // This fallback derives its figures from the hunts it was handed, which
      // carry no wounded reports — only the season-stats endpoint behind the
      // seasonSection slot knows about those. Zero is the honest value here, not
      // a placeholder.
      wounded={0}
    />
  )

  const defaultActivity = (
    <ActivityWeekCard
      bars={[3, 5, 4, 7, 6, 9, 2]}
      gameReported="47"
      hoursInField="28h"
      mapPins="112"
      participants="26"
    />
  )

  return (
    <div style={{ padding: '0 0 40px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 500, margin: '0 0 4px', color: COLORS.onSurface, fontFamily: COLORS.fontDisplay, lineHeight: 1.1 }}>
          {greeting(t, user.name)}
        </h1>
        <p style={{ margin: 0, color: COLORS.outline, fontSize: 14 }}>
          {t('subtitle')}
        </p>
      </div>

      {topStatsSection ?? defaultTopStats}

      {seasonSection ?? defaultSeason}

      {/* CLUB-138: with the Activity panel hidden, drop the 2-column grid so the
          Upcoming hunts card reflows to full width (no empty half-column). */}
      <div className={showActivityPanel ? 'wh-grid-2col-eq' : undefined}>
        <div className="wh-card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface, whiteSpace: 'nowrap' }}>{t('upcoming.title')}</div>
            <a href="/hunts" style={{ fontSize: 12, fontWeight: 600, color: COLORS.primary, whiteSpace: 'nowrap' }}>{t('upcoming.viewAll')}</a>
          </div>
          {upcoming.length === 0 && ongoing.length === 0 ? (
            <p style={{ margin: '4px 0 0', fontSize: 14, color: COLORS.outline }}>
              {t('upcoming.empty')}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {ongoing.map((h) => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: 12, margin: '4px -8px 0', borderRadius: 8, background: 'rgba(255,104,31,0.06)' }}>
                  <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {h.quarry} · {h.team}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 8px', borderRadius: 999, background: 'rgba(255,104,31,0.12)', color: COLORS.onTertiaryFixedVariant, fontSize: 11, fontWeight: 600 }}>
                        <LiveDot size={7} />
                        {t('upcoming.ongoing')}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>
                      {t('upcoming.today')}, {h.start} · {t('upcoming.participants', { count: h.participants })}
                    </div>
                  </div>
                  <button className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => onOpenHunt(h.id)}>{t('upcoming.open')}</button>
                </div>
              ))}
              {upcoming.map((h, i) => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '12px 0', borderTop: (i > 0 || ongoing.length > 0) ? `1px solid ${COLORS.outlineVariant}` : 'none' }}>
                  <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {h.quarry} · {h.team}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {huntLabel(h)}, {h.start} · {t('upcoming.participants', { count: h.participants })}
                    </div>
                  </div>
                  <button className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => onOpenHunt(h.id)}>{t('upcoming.open')}</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {showActivityPanel && (activityPanel ?? defaultActivity)}
      </div>
    </div>
  )
}
